import { useState, useCallback } from 'react';
import Select from 'react-select';
import EmployeeService from '../services/EmployeeService';
import IEmployeeDetail from '../types/Employee/EmployeeDetail';

type Props = {
    dataName: string;
    title: string;
    hintText?: string;
    disabled?: boolean;
    required?: boolean;
    optionValue?: 'EmployeeID' | 'Email';
    onchange?: (e: any) => void;
    onEmployeeChange?: (e: IEmployeeDetail) => void;

    /** --- Styling props --- */
    className?: string;          // outer wrapper
    labelClassName?: string;     // label <label> text
    labelWrapperClassName?: string; // wrapper around label element
    hintClassName?: string;      // <p> hint text
    selectClassName?: string;    // react-select component
};

type SelectOption = { value: string; label: string } | null;

export default function ADSearchField({
    dataName,
    title,
    hintText,
    disabled,
    required,
    optionValue,
    onchange,
    onEmployeeChange,
    className,
    labelClassName,
    labelWrapperClassName,
    hintClassName,
    selectClassName,
}: Props) {
    const [employees, setEmployees] = useState<IEmployeeDetail[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [selectedOption, setSelectedOption] = useState<SelectOption>(null);
    const [openMenu, setOpenMenu] = useState(false);

    const getEmployees = useCallback(async (searchString: string) => {
        try {
            const response = await EmployeeService.getEmployees(searchString);
            setEmployees(response.data);
            setOpenMenu(true);
        } catch (e) {
            console.error(e);
        }
    }, []);

    const handleInputChange = (input: string) => {
        setSelectedEmployee(input);

        if (input.trim().length > 0) {
            getEmployees(input);
        } else {
            setEmployees([]);
            setOpenMenu(false);
        }
    };

    const handleSelectChange = (event: any) => {
        const value = event?.value ?? '';
        const selected = employees.find(emp =>
            optionValue === 'Email'
                ? emp.employeeEmail === value
                : emp.employeeID === value
        );

        setSelectedOption(event ?? null);
        setOpenMenu(false);

        onchange?.(value);

        if (selected && onEmployeeChange) {
            onEmployeeChange(selected);
        }
    };

    const hideMenu = () => setOpenMenu(false);

    const options = employees.map(emp => ({
        value: optionValue === 'Email' ? emp.employeeEmail : emp.employeeID,
        label: `${emp.employeeName} (${emp.employeeID})`,
    }));

    return (
        <div className={`form-group ComponentDivs${className ? ' ' + className : ''}`}>
            {title && (
                <div className={labelWrapperClassName}>
                    <b>
                        <label htmlFor={dataName} className={labelClassName}>
                            {title}{' '}
                            {required && <span className="required">*</span>}
                        </label>
                    </b>
                    <br />
                </div>
            )}

            {hintText && (
                <p className={`HintText${hintClassName ? ' ' + hintClassName : ''}`}>
                    {hintText}
                </p>
            )}

            <Select
                className={selectClassName}
                placeholder="Search Name..."
                name={dataName}
                inputValue={selectedEmployee}
                value={selectedOption}
                onInputChange={handleInputChange}
                onChange={handleSelectChange}
                onBlur={hideMenu}
                options={options}
                menuIsOpen={openMenu}
                components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                }}
                isClearable
                isDisabled={disabled}
                autoFocus
            />
        </div>
    );
}
